import {cloneDeep, get, isEmpty, omit, set} from 'lodash';

const initialState = {
    activeTab: undefined,
    bulkMapping: false,
    bulkMappingType: undefined,
    error: undefined,
    fetched: false,
    fetching: false,
    flaggedRoviProgramId: {},
    flaggedTmsId: {},
    lastMassItem: undefined,
    mappingData: undefined,
    massMapping: false,
    massShell: false,
    massShellActive: false,
    mediaItemData: {},
    mediaItems: undefined,
    mediaItemsFilter: undefined,
    rememberMappingData: {},
    response: undefined,
    routerParams: {},
    shellContentType: undefined,
    showMessage: false,
    showRememberMapping: false,
    unmappedMediaItems: undefined,
    unmappedMediaItemsFilter: undefined,
    validShell: undefined,
};

const actions = {
    MAPPING_POST: (state) => ({
        ...state,
        fetched: false,
        fetching: true,
    }),

    MAPPING_POST_FULFILLED: (state, {response}) => ({
        ...state,
        error: null,
        fetched: true,
        fetching: false,
        showMessage: true,
        response,
    }),

    MAPPING_POST_REJECTED: (state, {error}) => {
        const {
            activeTab,
            mediaItemData,
            mediaItems,
            mediaItemsFilter,
            rememberMappingData,
            routerParams,
            unmappedMediaItems,
            unmappedMediaItemsFilter,
        } = cloneDeep(state);

        return {
            ...cloneDeep(initialState),
            activeTab,
            mediaItemData,
            mediaItems,
            mediaItemsFilter,
            rememberMappingData,
            routerParams,
            unmappedMediaItems,
            unmappedMediaItemsFilter,
            fetched: true,
            error,
        };
    },

    MAPPING_REPLACE_DATA: (state, {mappingData, showMessage}) => ({
        ...state,
        mappingData,
        showMessage,
    }),

    MAPPING_SET_MASS_MAPPING: (state, {massMapping}) => ({
        ...state,
        massMapping,
    }),

    MAPPING_SET_MASS_SHELL: (state, {massShell}) => ({
        ...state,
        massShell,
    }),

    MAPPING_SET_BULK_MAPPING: (state, {bulkMapping, bulkMappingType}) => ({
        ...state,
        bulkMapping,
        bulkMappingType,
    }),

    MAPPING_SET_MASS_SHELL_ACTIVE: (state, {massShellActive}) => ({
        ...state,
        massShellActive,
    }),

    MAPPING_SET_SHELL_CONTENT_TYPE: (state, {shellContentType}) => ({
        ...state,
        shellContentType,
    }),

    MAPPING_SET_FLAGGED_ROVI_TMSID_PROGRAM_ID: (state, {flaggedRoviProgramId, flaggedTmsId, flaggedSourceId}) => ({
        ...state,
        flaggedRoviProgramId: {
            ...state.flaggedRoviProgramId,
            ...flaggedRoviProgramId,
        },
        flaggedTmsId: {
            ...state.flaggedTmsId,
            ...flaggedTmsId,
        },
        flaggedSourceId: {
            ...state.flaggedSourceId,
            ...flaggedSourceId,
        },
    }),

    MAPPING_SET_MAPPING_DATA: (state, {mappingData, replaceMappingData, showMessage}) => ({
        ...state,
        mappingData: replaceMappingData ? mappingData : {...state.mappingData, ...mappingData},
        showMessage,
    }),

    MAPPING_SET_SHOW_MESSAGE: (state, {showMessage}) => ({...state, showMessage: !!showMessage}),

    MAPPING_SET_SHOW_REMEMBER_MAPPING: (state, {showRememberMapping}) => ({
        ...state,
        showRememberMapping: !!showRememberMapping,
    }),

    MAPPING_RESET_CONTENT_TYPE: (state, {contentType, showMessage}) => {
        const newState = cloneDeep(state);

        set(newState, 'mappingData', omit(state.mappingData, contentType));
        set(newState, 'showMessage', !!showMessage);

        return newState;
    },

    MAPPING_RESET: (state, {fullReset}) => {
        const {
            activeTab,
            bulkMapping,
            bulkMappingType,
            massMapping,
            massShellActive,
            mediaItemData,
            mediaItems,
            mediaItemsFilter,
            rememberMappingData,
            routerParams,
            unmappedMediaItems,
            unmappedMediaItemsFilter,
        } = cloneDeep(state);
        const newState = cloneDeep(initialState);

        if (!fullReset) {
            if (massMapping) {
                set(newState, 'mappingData.franchise', get(state, 'mappingData.franchise'));
                newState.massMapping = true;
            } else if (massShellActive) {
                newState.massShellActive = true;
            }

            if (bulkMapping) {
                set(newState, 'mappingData.franchise', get(state, 'mappingData.franchise'));
                newState.bulkMapping = bulkMapping;
                newState.bulkMappingType = bulkMappingType;
            }
        }

        return {
            ...newState,
            activeTab,
            mediaItemData,
            mediaItems,
            mediaItemsFilter,
            rememberMappingData,
            routerParams,
            unmappedMediaItems,
            unmappedMediaItemsFilter,
        };
    },

    MAPPING_SET_VALID_SHELL: (state) => ({
        ...cloneDeep(state),
        validShell: true,
    }),

    MAPPING_SET_LAST_MASS_ITEM: (state, {lastMassItem = true}) => ({
        ...cloneDeep(state),
        lastMassItem,
    }),

    MAPPING_SET_ACTIVE_TAB: (state, {activeTab}) => ({
        ...cloneDeep(state),
        activeTab,
    }),

    SEARCH_DATA_FETCH_FULFILLED: (state, {searchData, servicesDataKey}) => {
        const newState = cloneDeep(state);

        if (servicesDataKey === 'mediaItemDetail') {
            set(newState, 'mediaItemData', get(searchData, 'content.0') || {});
        } else if (servicesDataKey === 'mediaItems') {
            set(newState, 'mediaItems', get(searchData, 'content'));
        } else if (servicesDataKey === 'unmappedMediaItems') {
            set(newState, 'unmappedMediaItems', get(searchData, 'content'));
        }

        return newState;
    },

    FORM_SET_FIELD_VALUE: (state, {formName, fieldName, value}) => {
        const newState = cloneDeep(state);
        const newValue = value !== '' ? value : undefined;

        if (formName === 'rememberMapping') {
            set(newState, `rememberMappingData.${fieldName}`, newValue);
        }

        return newState;
    },

    FORM_SET_FIELD_ERRORS: (state, {formName, errors}) => {
        const newState = cloneDeep(state);

        if (formName === 'createShell' && !isEmpty(errors)) {
            set(newState, 'validShell', false);
        }

        return newState;
    },

    FORM_SET_FIELD_VALUES: (state, {formName, values, resetValues}) => {
        const newState = cloneDeep(state);

        if (formName === 'rememberMapping') {
            const formValues = resetValues ? values : {...get(newState, 'rememberMappingData'), ...values};

            set(newState, 'rememberMappingData', formValues);
        }

        return newState;
    },

    SEARCH_DATA_SET_FILTER: (state, {servicesDataKey, targets}) => {
        const newState = cloneDeep(state);

        if (servicesDataKey === 'mediaItems') {
            set(newState, 'mediaItemsFilter', targets);
        } else if (servicesDataKey === 'unmappedMediaItems') {
            set(newState, 'unmappedMediaItemsFilter', targets);
        }

        return newState;
    },

    ROUTER_UPDATE: (state, {routerParams}) => ({
        ...state,
        routerParams,
    }),
};

const reducer = (state = initialState, action) => (actions[action.type] ? actions[action.type](state, action) : state);

export default reducer;
